import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { BehaviorSubject, Observable, of, retry } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';
import { NOT_CANCELLED_HEADER } from '../interceptors/auth.interceptor';

export interface IStore {
	id: number;
	name: string;
	markers?: Array<number>;
	group?: number;
}

export interface IStoreUseInfo {
	id: number;
	name: string;
	used: boolean;
}

@Injectable({
	providedIn: 'root'
})
export class StoresService {
	private _getShopsSubject: BehaviorSubject<Array<IStore>> = new BehaviorSubject<Array<IStore>>(null);
	private _loading: boolean = false;

	get getShops$(): Observable<Array<IStore>> {
		if (!this._getShopsSubject.value && !this._loading) {
			this._get()
				.pipe(
					retry({
						count: 5,
						delay: 1000
					}),
					catchError(() => of([]))
				)
				.subscribe((response: Array<IStore>) => {
					this._getShopsSubject.next(response);
				});
		}
		this._loading = true;
		return this._getShopsSubject.pipe(
			filter((value: Array<IStore>) => value !== null)
		);
	}

	constructor(
		private http: HttpClient,
		private httpService: HttpService
	) {}

	private _createTree(data: Array<any>, id: number): Array<any> {
		const markers = data.filter(m => m.parent_id == id) || [];
		markers.forEach(m => {
			m.children = this._createTree(data, m.id);
			m.has_children = !!m.children.length;
			m.loading = false;
		});
		return markers;
	}

	private _get(): Observable<Array<IStore>> {
		return this.http.get<Array<IStore>>(
            '/api/data/shops/?fields=id,name,group,markers', 
            { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') }
        );
	}

	getShopGroups(): Promise<Array<any>> {
		return this.httpService.getPromise<Array<any>>(
			this.http.get<Array<any>>('/api/data/shop-groups/get_names/')
		);
	}

	getShopMarkers(): Promise<Array<any>> {
		return this.httpService.getPromise<Array<any>>(
			this.http.get<Array<any>>('/api/data/shop-markers/get_names/')
		)
			.then(result => this._createTree(result, null))
			.then(result => result.length ? result[0].children : []);
	}

	getShopsUseInfo(): Observable<Array<IStoreUseInfo>> {
		return this.http.get<Array<IStoreUseInfo>>('/api/data/shops/?fields=id,name,used');
	}

	getUnusedShops(): Observable<Array<IStore>> {
		return this.http.get<Array<IStore>>('/api/data/shops/?fields=id,name&used=false');
	}
}
